import { defaultThemeV2 } from "../../theme/defaultThemeV2";
import type { QuestionCore } from "../contracts";
import { encodeResponses } from "../responseEncoding";

export type MaxDiffRaw = {
  type: "max_diff";
  content: string;
  options: { value: "left" | "right"; text: string; disabled: boolean }[];
  rows: {
    value: string;
    text: string;
    image?: string | null;
    display_text: boolean;
  }[];
  dont_know_option: { value: string; text: string } | null;
  previous_response?: string[];
  image?: string | null;
  alt_text?: string | null;
  error_message?: string;
};

export type MaxDiffDefinition = {
  prompt: string;
  options: readonly {
    id: "left" | "right";
    label: string;
    disabled: boolean;
  }[];
  rows: readonly {
    id: string;
    label: string;
    image?: string;
    displayLabel: boolean;
  }[];
  dontKnow?: { value: string; label: string };
  previousResponses: readonly string[];
  image?: string;
  altText?: string;
  error?: string;
};

export type MaxDiffDraft = {
  selected: Readonly<{ left: string | null; right: string | null }>;
  dontKnow: boolean;
};
export type MaxDiffEvent =
  | { type: "select"; column: "left" | "right"; rowId: string }
  | { type: "dont-know" };
export type MaxDiffResponse = {
  type: "max_diff";
  value: string;
  responses?: string;
};

export const maxDiffCore: QuestionCore<
  MaxDiffRaw,
  MaxDiffDefinition,
  MaxDiffDraft,
  MaxDiffEvent,
  MaxDiffResponse
> = {
  decode: (raw) => {
    if (
      !Array.isArray(raw.options) ||
      raw.options.length !== 2 ||
      raw.options[0]?.value !== "left" ||
      raw.options[1]?.value !== "right"
    ) {
      throw new Error("Max diff requires ordered left and right labels.");
    }
    if (!Array.isArray(raw.rows) || raw.rows.length < 2) {
      throw new Error("Max diff requires at least two items.");
    }
    const rowIds = raw.rows.map((row) => row.value);
    if (
      rowIds.some((id) => typeof id !== "string" || !id) ||
      new Set(rowIds).size !== rowIds.length
    ) {
      throw new Error("Max diff item values must be unique.");
    }
    if (
      raw.previous_response !== undefined &&
      (!Array.isArray(raw.previous_response) ||
        raw.previous_response.some((value) => typeof value !== "string"))
    ) {
      throw new Error("Max diff previous response must be a list of strings.");
    }
    return {
      prompt: raw.content,
      options: raw.options.map((option) => ({
        id: option.value,
        label: option.text,
        disabled: option.disabled,
      })),
      rows: raw.rows.map((row) => ({
        id: row.value,
        label: row.text,
        displayLabel: row.display_text,
        ...(row.image ? { image: row.image } : {}),
      })),
      previousResponses: raw.previous_response ?? [],
      ...(raw.dont_know_option
        ? {
            dontKnow: {
              value: raw.dont_know_option.value,
              label: raw.dont_know_option.text,
            },
          }
        : {}),
      ...(raw.image ? { image: raw.image } : {}),
      ...(raw.alt_text ? { altText: raw.alt_text } : {}),
      ...(raw.error_message ? { error: raw.error_message } : {}),
    };
  },
  initialize: (definition) => {
    const [left, right] = definition.previousResponses;
    if (
      definition.dontKnow &&
      left === definition.dontKnow.value &&
      right === definition.dontKnow.value
    ) {
      return { selected: { left: null, right: null }, dontKnow: true };
    }
    const valid = (value: string | undefined) =>
      definition.rows.some((row) => row.id === value) ? value! : null;
    const selectedLeft = definition.options[0].disabled ? null : valid(left);
    const selectedRight = valid(right);
    return {
      selected: {
        left: selectedLeft,
        right:
          definition.options[1].disabled || selectedRight === selectedLeft
            ? null
            : selectedRight,
      },
      dontKnow: false,
    };
  },
  update: (definition, draft, event) => {
    if (event.type === "dont-know") {
      return definition.dontKnow
        ? { selected: { left: null, right: null }, dontKnow: true }
        : draft;
    }
    if (!definition.rows.some((row) => row.id === event.rowId)) return draft;
    if (
      definition.options.find((option) => option.id === event.column)?.disabled
    )
      return draft;
    const other = event.column === "left" ? "right" : "left";
    if (draft.selected[other] === event.rowId) return draft;
    if (
      draft.selected[event.column] !== null &&
      draft.selected[event.column] !== event.rowId
    )
      return draft;
    return {
      selected: {
        ...draft.selected,
        [event.column]:
          draft.selected[event.column] === event.rowId ? null : event.rowId,
      },
      dontKnow: false,
    };
  },
  validate: (definition, draft, copy = defaultThemeV2.copy) => {
    if (draft.dontKnow && definition.dontKnow) return { valid: true };
    const { left, right } = draft.selected;
    return left &&
      right &&
      left !== right &&
      definition.options.every((option) => !option.disabled) &&
      definition.rows.some((row) => row.id === left) &&
      definition.rows.some((row) => row.id === right)
      ? { valid: true }
      : { valid: false, error: copy.validation.chooseTwo };
  },
  encode: (definition, draft) => {
    const validation = maxDiffCore.validate(definition, draft);
    if (!validation.valid) throw new Error(validation.error);
    if (draft.dontKnow)
      return { type: "max_diff", value: definition.dontKnow!.value };
    const values = definition.options.map(
      (option) => draft.selected[option.id]!,
    );
    return {
      type: "max_diff",
      value: values.join(","),
      responses: encodeResponses(values),
    };
  },
  projectTranscript: (definition, draft) => {
    const validation = maxDiffCore.validate(definition, draft);
    if (!validation.valid) throw new Error(validation.error);
    return {
      kind: "max_diff",
      question: definition.prompt,
      answer: draft.dontKnow
        ? definition.dontKnow!.label
        : definition.options
            .map(
              (option) =>
                `${option.label}: ${definition.rows.find((row) => row.id === draft.selected[option.id])!.label}`,
            )
            .join("\n"),
    };
  },
};
